import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { DraftTrackerLeaderboardTable } from './table/DraftTrackerLeaderboardTable';
import {
    ClubNavItem,
    positions,
    PositionsNavItem,
    RoundNavItem,
    Search
} from './nav-items';
import { Legend } from './table/components/Legend';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useGetDraftByYearQuery } from './api/draft-tracker-api';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { draftTrackerNavSelector } from 'store/modules/draft-tracker/leaderboard/selectors';
import { FilterButton } from 'common/react/components/FilterButton';
import { getTranslation } from 'common/react/utils/translations';

export const DraftTrackerLeaderboard = ({
    seasonPid,
    seasonId,
    title,
    startDateTime
}) => {
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);
    document.body.classList.add('remove-sticky-nav');

    // eslint-disable-next-line no-magic-numbers
    const year = seasonPid.substring(4, 8);

    const windowSize = useWindowSizeV2();
    const dispatch = useDispatch();

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const { data: draftData } = useGetDraftByYearQuery(year);

    const nav = useSelector(draftTrackerNavSelector);
    const statsSectionNavRef = useRef();

    /**
     * custom hook providing tracking of filters
     * because useFiltersTracking expects `nav` to have the structure as `state.competitionNav.nav` we need to craft the correct object structure
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            season: { id: seasonId },
            round: { roundNumber: nav.round },
            filters: {
                teams: [nav.club]
            },
            position: { id: nav.position }
        },
        options: {
            positions: positions
        },
        filtersFields: ['round', 'teams', 'position'],
        filtersRef: statsSectionNavRef
    });

    /**
     * Gets the initial round ID from the default state.
     *
     * @returns {number} The initial round ID (-1 for all rounds)
     */
    const getInitialRoundId = () => {
        return -1; // Default from initial state
    };

    /**
     * Gets the initial club ID from the default state.
     *
     * @returns {number} The initial club ID (-1 for all clubs)
     */
    const getInitialClubId = () => {
        return -1; // Default from initial state
    };

    /**
     * Gets the initial position ID from the default state.
     *
     * @returns {number} The initial position ID (-1 for all positions)
     */
    const getInitialPositionId = () => {
        return -1; // Default from initial state
    };

    /**
     * Gets the initial filter string from the default state.
     *
     * @returns {string} The initial filter string (empty string)
     */
    const getInitialFilter = () => {
        return ''; // Default from initial state
    };

    /**
     * Checks if any navigation filters have changed from their initial default values.
     *
     * @returns {boolean} True if any filter has changed from defaults
     */
    const hasChangedFromDefaults = () => {
        if (!nav) {
            return false;
        }

        const initialRoundId = getInitialRoundId();
        const initialClubId = getInitialClubId();
        const initialPositionId = getInitialPositionId();
        const initialFilter = getInitialFilter();

        const isRoundChanged = nav.round !== initialRoundId;
        const isClubChanged = nav.club !== initialClubId;
        const isPositionChanged = nav.position !== initialPositionId;
        const isFilterChanged = nav.filter !== initialFilter;

        return (
            isRoundChanged ||
            isClubChanged ||
            isPositionChanged ||
            isFilterChanged
        );
    };

    /**
     * Resets all navigation filters back to their initial default values.
     */
    const resetToDefaults = () => {
        const initialRoundId = getInitialRoundId();
        const initialClubId = getInitialClubId();
        const initialPositionId = getInitialPositionId();
        const initialFilter = getInitialFilter();

        if (nav.round !== initialRoundId) {
            dispatch(
                PULSE.app.redux.actions.draftTracker.updateNav({
                    name: 'round',
                    value: initialRoundId
                })
            );
        }
        if (nav.club !== initialClubId) {
            dispatch(
                PULSE.app.redux.actions.draftTracker.updateNav({
                    name: 'club',
                    value: initialClubId
                })
            );
        }
        if (nav.position !== initialPositionId) {
            dispatch(
                PULSE.app.redux.actions.draftTracker.updateNav({
                    name: 'position',
                    value: initialPositionId
                })
            );
        }
        if (nav.filter !== initialFilter) {
            dispatch(
                PULSE.app.redux.actions.draftTracker.updateNav({
                    name: 'filter',
                    value: initialFilter
                })
            );
        }
    };

    const classPrefix = 'brownlow-leaderboard-predictor brownlow-leaderboard';

    const renderMobile = () => (
        <>
            <StatsSection.NavItems>
                {draftData?.selections?.length && (
                    <RoundNavItem year={year} classes="round-nav-item" />
                )}
                <div className="draft_tracker__buttons-wrapper">
                    <FilterButton
                        classes="fixtures-nav__filter-button"
                        onChange={() =>
                            setAreFilterOptionsVisible((prev) => !prev)
                        }
                        areFilterOptionsVisible={areFilterOptionsVisible}
                        resetFilterBtnStyles={resetFilterBtnStyles}
                        setResetFilterBtnStyles={setResetFilterBtnStyles}
                        showFilterLabel={false}
                        showActiveCircle
                    />
                    <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                        <SettingsButton />
                    </StatsSection.SettingsButton>
                </div>
            </StatsSection.NavItems>
            {areFilterOptionsVisible ? (
                <StatsSection.NavFilterItems>
                    {draftData?.selections?.length && (
                        <ClubNavItem seasonId={seasonId} year={year} />
                    )}

                    {draftData?.selections?.length ? (
                        <PositionsNavItem year={year} title={title} />
                    ) : (
                        <PositionsNavItem />
                    )}
                    {hasChangedFromDefaults() ? (
                        <div
                            className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset"
                            style={{
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <button
                                className="stats-leaders-filter-reset-button"
                                onClick={resetToDefaults}
                            >
                                {getTranslation(
                                    'label.statsLeaders.filters.resetButtonText'
                                )}
                            </button>
                        </div>
                    ) : null}
                </StatsSection.NavFilterItems>
            ) : null}

            <StatsSection.SearchBarMobile>
                <div
                    className={`${classPrefix}-nav__extras`}
                    style={{
                        borderTop: 'unset'
                    }}
                >
                    <Search />
                </div>
            </StatsSection.SearchBarMobile>
        </>
    );

    const renderMobilePreDraft = () => (
        <>
            <StatsSection.NavItems>
                {draftData?.selections?.length ? (
                    <PositionsNavItem
                        year={year}
                        title={title}
                        classes="round-nav-item"
                    />
                ) : (
                    <PositionsNavItem classes="round-nav-item" />
                )}
                {hasChangedFromDefaults() ? (
                    <div
                        className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset"
                        style={{
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <button
                            className="stats-leaders-filter-reset-button"
                            onClick={resetToDefaults}
                        >
                            {getTranslation(
                                'label.statsLeaders.filters.resetButtonText'
                            )}
                        </button>
                    </div>
                ) : null}
                <div className="draft_tracker__buttons-wrapper">
                    <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                        <SettingsButton />
                    </StatsSection.SettingsButton>
                </div>
            </StatsSection.NavItems>

            <StatsSection.SearchBarMobile>
                <div
                    className={`${classPrefix}-nav__extras`}
                    style={{
                        borderTop: 'unset'
                    }}
                >
                    <Search />
                </div>
            </StatsSection.SearchBarMobile>
        </>
    );

    const renderDesktop = (preDraft = false) => (
        <>
            <StatsSection.NavItems>
                {!preDraft && draftData?.selections?.length && (
                    <RoundNavItem year={year} />
                )}
                {!preDraft && draftData?.selections?.length && (
                    <ClubNavItem seasonId={seasonId} year={year} />
                )}
                <PositionsNavItem
                    className={
                        draftData?.selections?.length ? 'select--rounded' : ''
                    }
                />
                {hasChangedFromDefaults() && !showResponsiveItems ? (
                    <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                        <button
                            className="stats-leaders-filter-reset-button"
                            onClick={resetToDefaults}
                        >
                            {getTranslation(
                                'label.statsLeaders.filters.resetButtonText'
                            )}
                        </button>
                    </div>
                ) : null}
            </StatsSection.NavItems>
            <StatsSection.SettingsButton>
                <div className={`${classPrefix}-nav__extras`}>
                    <Search />
                </div>
                <SettingsButton />
            </StatsSection.SettingsButton>
        </>
    );

    const renderFilters = () =>
        showResponsiveItems ? renderMobile() : renderDesktop();

    const renderFiltersPreDraft = () =>
        showResponsiveItems ? renderMobilePreDraft() : renderDesktop(true);

    return (
        <>
            <div
                className="competition-nav competition-nav--no-margin-top filter-dropdown"
                ref={statsSectionNavRef}
            >
                <StatsSection.Main>
                    <StatsSection.Nav
                        showResponsiveItems={showResponsiveItems}
                        withScrollableFilter={!showResponsiveItems}
                    >
                        {draftData?.status ===
                        PULSE.app.common.CONSTANTS.DRAFT_STATUS.PRE_DRAFT
                            ? renderFiltersPreDraft()
                            : renderFilters()}
                    </StatsSection.Nav>
                </StatsSection.Main>
            </div>
            <div className="wrapper draft-tracker--wrapper">
                <DraftTrackerLeaderboardTable
                    year={year}
                    title={title}
                    startDateTime={startDateTime}
                />
                <Legend />
            </div>
        </>
    );
};

DraftTrackerLeaderboard.propTypes = {
    seasonPid: PropTypes.string.isRequired,
    seasonId: PropTypes.string.isRequired,
    title: PropTypes.string,
    startDateTime: PropTypes.string
};
